// Headlight templates for the PSF `Link` InputType. PSF ships bare defaults (no Bulma wrapper,
// no Read/Print variants); these add the house style across Edit/Read/Print for Standard, Vertical,
// and Tabular layouts. (RecordSet reuses the Tabular entries — it is not a separate template axis.)
//
// Contract preserved from pict-section-form's Link provider (Pict-Provider-Input-Link.js):
//   - Standard/Vertical: the anchor MUST carry id="INPUT-FOR-{RawHTMLID}" — onDataMarshalToForm
//     sets its `href` there. Visible text is the field Name (the Name is the link caption).
//   - Tabular: the anchor MUST carry id="TABULAR-DATA-{RawHTMLID}-{rowIndex}" — onDataMarshalToFormTabular
//     sets `href` there. `{~D:Context[2].Key~}` resolves to the row index.
//   - The hidden input carries the informary binding; Link is display-only for its URL, so there is
//     no change handler (matching PSF — the value arrives from data/solvers, not from editing the anchor).
//   - Every anchor carries {~LinkTarget:Record~}, which emits target/rel when the descriptor sets
//     PictForm.LinkTarget. Absent, it emits nothing (same-tab default). All nine anchors must keep
//     it in lockstep across Edit/Read/Print -- the template tests enforce that.
//   - The Tabular Begin/End split is deliberate: Begin opens the hidden input tag and leaves it
//     unclosed after the informary macros so the framework's Mid partial can inject binding attributes;
//     End closes it and renders the anchor. Do not close the input inside Begin.
const _TemplatesGroupedByTheme =
{
	/* EDIT THEME - Interactive mode for browsers */
	Edit: [
		{
			HashPostfix: '-Template-Input-InputType-Link',
			DefaultInputExtensions: [ 'Pict-Input-Link' ],
			Template: /*HTML*/`
			<!-- InputType Link {~D:Record.Hash~} {~D:Record.DataType~} -->
			<div class="column {~ColumnWidth:Record~}">
				<input type="hidden" tabindex="-1" {~D:Record.Macro.InputFullProperties~} value="">
				<a {~D:Record.Macro.ControlAttr~} id="INPUT-FOR-{~D:Record.Macro.RawHTMLID~}" class="pict-section-form-link" {~LinkTarget:Record~}>
					<span class="icon"><i class="fas fa-fw fa-external-link-alt"></i></span>
					<span>{~D:Record.Name~}</span>
				</a>
				<p class="help"><span class="description-text">{~D:Record.Description~}</span></p>
			</div>`,
		},
		{
			HashPostfix: '-VerticalTemplate-Input-InputType-Link',
			DefaultInputExtensions: [ 'Pict-Input-Link' ],
			Template: /*HTML*/`
			<!-- InputType Link {~D:Record.Hash~} {~D:Record.DataType~} -->
			<div class="pict-form-vertical-input column pb-3">
				<div class="columns is-mobile is-vcentered">
					<div class="column">
						<label {~D:Record.Macro.HTMLForID~} class="label {~HideLabel:Record~} {~LabelAlignment:Record~}">{~LabelWithChips:Record~}</label>
					</div>
					<div class="column description-text">{~D:Record.Description~}</div>
					<div class="column">
						<input type="hidden" tabindex="-1" {~D:Record.Macro.InputFullProperties~} value="">
						<a {~D:Record.Macro.ControlAttr~} id="INPUT-FOR-{~D:Record.Macro.RawHTMLID~}" class="pict-section-form-link" {~LinkTarget:Record~}>
							<span class="icon"><i class="fas fa-fw fa-external-link-alt"></i></span>
							<span>{~D:Record.Name~}</span>
						</a>
					</div>
				</div>
			</div>`,
		},
		{
			HashPostfix: '-TabularTemplate-Begin-Input-InputType-Link',
			DefaultInputExtensions: [ 'Pict-Input-Link' ],
			Template: /*HTML-Partial*/`
			<!-- InputType Link {~D:Record.Hash~} {~D:Record.DataType~} -->
			<input type="hidden" {~D:Record.Macro.HTMLName~} {~D:Record.Macro.InformaryTabular~} `,
		},
		{
			HashPostfix: '-TabularTemplate-End-Input-InputType-Link',
			Template: /*HTML-Partial*/` value="">
			<a {~D:Record.Macro.ControlAttr~} id="TABULAR-DATA-{~D:Record.Macro.RawHTMLID~}-{~D:Context[2].Key~}" class="pict-section-form-link" {~D:Record.Macro.HTMLName~} {~LinkTarget:Record~}>{~D:Record.Name~}</a>`,
		},
	],
	/* READ THEME - Interactive mode for browsers in a locked/disabled/readonly state */
	Read: [
		{
			HashPostfix: '-Template-Input-InputType-Link',
			DefaultInputExtensions: [ 'Pict-Input-Link' ],
			Template: /*HTML*/`
			<!-- InputType Link {~D:Record.Hash~} {~D:Record.DataType~} -->
			<div class="column {~ColumnWidth:Record~}">
				<input type="hidden" tabindex="-1" {~D:Record.Macro.InputFullProperties~} value="">
				<a {~D:Record.Macro.ControlAttr~} id="INPUT-FOR-{~D:Record.Macro.RawHTMLID~}" class="pict-section-form-link" {~LinkTarget:Record~}>
					<span class="icon"><i class="fas fa-fw fa-external-link-alt"></i></span>
					<span>{~D:Record.Name~}</span>
				</a>
				<p class="help"><span class="description-text">{~D:Record.Description~}</span></p>
			</div>`,
		},
		{
			HashPostfix: '-VerticalTemplate-Input-InputType-Link',
			DefaultInputExtensions: [ 'Pict-Input-Link' ],
			Template: /*HTML*/`
			<!-- InputType Link {~D:Record.Hash~} {~D:Record.DataType~} -->
			<div class="pict-form-vertical-input column pb-3">
				<div class="columns is-mobile is-vcentered">
					<div class="column">
						<label {~D:Record.Macro.HTMLForID~} class="label {~HideLabel:Record~} {~LabelAlignment:Record~}">{~LabelWithChips:Record~}</label>
					</div>
					<div class="column description-text">{~D:Record.Description~}</div>
					<div class="column">
						<input type="hidden" tabindex="-1" {~D:Record.Macro.InputFullProperties~} value="">
						<a {~D:Record.Macro.ControlAttr~} id="INPUT-FOR-{~D:Record.Macro.RawHTMLID~}" class="pict-section-form-link" {~LinkTarget:Record~}>
							<span class="icon"><i class="fas fa-fw fa-external-link-alt"></i></span>
							<span>{~D:Record.Name~}</span>
						</a>
					</div>
				</div>
			</div>`,
		},
		{
			HashPostfix: '-TabularTemplate-Begin-Input-InputType-Link',
			DefaultInputExtensions: [ 'Pict-Input-Link' ],
			Template: /*HTML-Partial*/`
			<!-- InputType Link {~D:Record.Hash~} {~D:Record.DataType~} -->
			<input type="hidden" {~D:Record.Macro.HTMLName~} {~D:Record.Macro.InformaryTabular~} `,
		},
		{
			HashPostfix: '-TabularTemplate-End-Input-InputType-Link',
			Template: /*HTML-Partial*/` value="">
			<a {~D:Record.Macro.ControlAttr~} id="TABULAR-DATA-{~D:Record.Macro.RawHTMLID~}-{~D:Context[2].Key~}" class="pict-section-form-link" {~D:Record.Macro.HTMLName~} {~LinkTarget:Record~}>{~D:Record.Name~}</a>`,
		},
	],
	/* PRINT THEME - Non-interactive mode for PDF documents */
	Print: [
		{
			HashPostfix: '-Template-Input-InputType-Link',
			DefaultInputExtensions: [ 'Pict-Input-Link' ],
			Template: /*HTML*/`
			<!-- InputType Link {~D:Record.Hash~} {~D:Record.DataType~} -->
			<div class="column {~ColumnWidth:Record~} {~HideInPDF:Record~}">
				<input type="hidden" tabindex="-1" {~D:Record.Macro.InputFullProperties~} value="">
				<a {~D:Record.Macro.ControlAttr~} id="INPUT-FOR-{~D:Record.Macro.RawHTMLID~}" class="pict-section-form-link" {~LinkTarget:Record~}>
					<span class="icon"><i class="fas fa-fw fa-external-link-alt"></i></span>
					<span>{~D:Record.Name~}</span>
				</a>
				<p class="help"><span class="description-text">{~D:Record.Description~}</span></p>
			</div>`,
		},
		{
			HashPostfix: '-VerticalTemplate-Input-InputType-Link',
			DefaultInputExtensions: [ 'Pict-Input-Link' ],
			Template: /*HTML*/`
			<!-- InputType Link {~D:Record.Hash~} {~D:Record.DataType~} -->
			<div class="pict-form-vertical-input column pb-1 {~HideInPDF:Record~}">
				<div class="columns is-mobile is-vcentered">
					<div class="column">
						<label {~D:Record.Macro.HTMLForID~} class="label {~HideLabel:Record~} {~LabelAlignment:Record~}">{~LabelWithChips:Record~}</label>
					</div>
					<div class="column description-text">{~D:Record.Description~}</div>
					<div class="column">
						<input type="hidden" tabindex="-1" {~D:Record.Macro.InputFullProperties~} value="">
						<a {~D:Record.Macro.ControlAttr~} id="INPUT-FOR-{~D:Record.Macro.RawHTMLID~}" class="pict-section-form-link" {~LinkTarget:Record~}>
							<span class="icon"><i class="fas fa-fw fa-external-link-alt"></i></span>
							<span>{~D:Record.Name~}</span>
						</a>
					</div>
				</div>
			</div>`,
		},
		{
			HashPostfix: '-TabularTemplate-Begin-Input-InputType-Link',
			DefaultInputExtensions: [ 'Pict-Input-Link' ],
			Template: /*HTML-Partial*/`
			<!-- InputType Link {~D:Record.Hash~} {~D:Record.DataType~} -->
			<input type="hidden" {~D:Record.Macro.HTMLName~} {~D:Record.Macro.InformaryTabular~} `,
		},
		{
			HashPostfix: '-TabularTemplate-End-Input-InputType-Link',
			Template: /*HTML-Partial*/` value="">
			<a {~D:Record.Macro.ControlAttr~} id="TABULAR-DATA-{~D:Record.Macro.RawHTMLID~}-{~D:Context[2].Key~}" class="pict-section-form-link" {~D:Record.Macro.HTMLName~} {~LinkTarget:Record~}>{~D:Record.Name~}</a>`,
		},
	],
};

module.exports = _TemplatesGroupedByTheme;
